import { useNavigate, useSearchParams } from "@solidjs/router";
import { createSignal, type JSX, onCleanup, Show } from "solid-js";
import type { PaymentMethodView } from "../../shared/api";
import { api } from "../api";
import { AppBar } from "../components/AppBar";
import { Button } from "../components/Button";
import { OperatedBy } from "../components/OperatedBy";
import { session } from "../state/session";
import { loadStripe, type StripeJs, stripePublishableKey } from "../stripe";

type Step = "choose" | "element" | "saved";
type Choice = "card" | "apple_pay";

/**
 * Figma 04b choose method → 04c Stripe sheet → 04d saved. Fake mode when no publishable key.
 * Apple Pay is fake (product decision): in fake mode it saves a fake method that reads
 * "Apple Pay", without any wallet API. With Stripe, both choices open the Payment Element (which
 * offers Apple Pay by itself on Safari once the domain is verified).
 */
export function Payment() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const vehicle = () => (typeof params.vehicle === "string" ? params.vehicle : undefined);
  const [step, setStep] = createSignal<Step>("choose");
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<string>();
  const [method, setMethod] = createSignal<PaymentMethodView>();
  // Figma 04b has Apple Pay selected.
  const [choice, setChoice] = createSignal<Choice>("apple_pay");
  let elementHost!: HTMLDivElement;
  let stripe: StripeJs | undefined;
  let elements: ReturnType<StripeJs["elements"]> | undefined;
  let paymentElement: { unmount(): void } | undefined;
  onCleanup(() => paymentElement?.unmount());

  const fail = (m: string) => {
    setError(m);
    setBusy(false);
  };

  const saved = async (view: PaymentMethodView) => {
    setMethod(view);
    await session.refetch();
    setStep("saved");
    setBusy(false);
  };

  const add = async () => {
    setBusy(true);
    setError(undefined);
    let clientSecret: string;
    try {
      ({ clientSecret } = await api.post<{ clientSecret: string }>("/payments/setup-intent"));
    } catch {
      return fail("Impossible de démarrer l'enregistrement.");
    }
    const key = stripePublishableKey();
    if (!key) {
      // PAYMENTS=fake: the BFF accepts the secret's intent id directly (PLAN.md §8);
      // the "_apple_pay" suffix makes the fake method an Apple Pay one.
      const id = clientSecret.replace(/_secret.*$/, "");
      try {
        return await saved(
          await api.post<PaymentMethodView>("/payments/confirm-setup", {
            setupIntentId: choice() === "apple_pay" ? `${id}_apple_pay` : id,
          }),
        );
      } catch {
        return fail("Enregistrement refusé.");
      }
    }
    try {
      stripe = await loadStripe(key);
      elements = stripe.elements({
        clientSecret,
        appearance: { variables: { colorPrimary: "#6d3e91", fontFamily: "Signika, sans-serif" } },
      });
      setStep("element");
      const pe = elements.create("payment", { layout: "tabs" });
      paymentElement = pe;
      pe.mount(elementHost);
      setBusy(false);
    } catch {
      fail("Le module de paiement n'a pas pu se charger.");
    }
  };

  const confirmElement = async () => {
    if (!stripe || !elements) return;
    setBusy(true);
    setError(undefined);
    const r = await stripe.confirmSetup({ elements, redirect: "if_required" });
    if (r.error || !r.setupIntent) return fail(r.error?.message ?? "Carte refusée.");
    try {
      await saved(
        await api.post<PaymentMethodView>("/payments/confirm-setup", {
          setupIntentId: r.setupIntent.id,
        }),
      );
    } catch {
      fail("Enregistrement refusé.");
    }
  };

  const later = () =>
    navigate(vehicle() ? `/map?vehicle=${encodeURIComponent(vehicle() as string)}` : "/map");

  return (
    <main class="screen bg-surface">
      <AppBar
        title="Moyen de paiement"
        back={step() === "saved" ? "/map" : later}
        close={step() === "saved"}
      />

      <Show when={step() !== "saved"}>
        <div class="screen-body px-[15px] pt-[22px] short:pt-3">
          <h1 class="px-[5px] text-[21px] font-medium leading-[1.25] tracking-[0.21px] text-violet">
            Enregistrez un moyen de paiement
          </h1>
          <p class="mt-2 px-[5px] text-[15px] leading-[1.25] tracking-[0.15px] text-text">
            Choisissez le moyen de paiement à utiliser pour vos trajets vélo.
          </p>
          <div
            class="mt-1 rounded-xl border border-border bg-white px-4"
            role="radiogroup"
            aria-label="Moyen de paiement"
          >
            <MethodRow
              glyph={<CardGlyph />}
              label="Carte bancaire"
              checked={choice() === "card"}
              disabled={step() !== "choose"}
              onSelect={() => setChoice("card")}
            />
            <div class="h-px bg-border" />
            <MethodRow
              glyph={<ApplePayBadge />}
              label="Apple Pay"
              checked={choice() === "apple_pay"}
              disabled={step() !== "choose"}
              onSelect={() => setChoice("apple_pay")}
            />
          </div>
          <div class="relative mt-[18px] rounded-xl bg-surface2 pb-[14px] pl-[45px] pr-[13px] pt-[15px]">
            <img
              src="/icons/info-22.svg"
              width="22"
              height="22"
              alt=""
              class="absolute left-[14px] top-[14px]"
            />
            <div class="text-[15px] font-medium leading-[1.25] tracking-[0.15px] text-violet">
              Empreinte temporaire
            </div>
            <p class="mt-1 text-[13px] leading-[1.25] tracking-[0.13px] text-text">
              Avant chaque trajet, une empreinte de 5 € est réalisée sur votre moyen de paiement. Le
              montant est ajusté au prix réel à la fin du trajet.
            </p>
          </div>
          <div ref={elementHost} class="mt-4" classList={{ hidden: step() !== "element" }} />
          <Show when={error()}>
            {(m) => (
              <p class="mt-3 px-[5px] text-sm text-[#e53935]" role="alert">
                {m()}
              </p>
            )}
          </Show>
          <div class="screen-footer flex flex-col gap-[10px]">
            <Show when={step() === "choose"}>
              <Button variant="outline" onClick={later}>
                Plus tard
              </Button>
              <Button loading={busy()} onClick={add}>
                Ajouter
              </Button>
            </Show>
            <Show when={step() === "element"}>
              <Button loading={busy()} onClick={confirmElement}>
                Enregistrer
              </Button>
            </Show>
          </div>
        </div>
      </Show>

      <Show when={step() === "saved"}>
        <div class="screen-body px-[15px] pt-[82px] text-center short:pt-8">
          <img
            src="/icons/check-circle-72.svg"
            width="72"
            height="72"
            alt=""
            class="mx-auto block"
          />
          <h1 class="mt-6 text-2xl font-medium leading-[1.25] tracking-[0.24px] text-violet">
            Paiement enregistré
          </h1>
          <p class="mx-auto mt-1 max-w-[322px] text-[15px] leading-[1.25] tracking-[0.15px] text-text">
            Vous pouvez maintenant scanner votre vélo et rouler.
          </p>
          <div class="mt-4 flex h-16 items-center gap-[15px] rounded-xl border border-border bg-white px-4 text-left">
            {method()?.wallet === "apple_pay" ? <ApplePayBadge /> : <CardGlyph />}
            <div class="min-w-0">
              <div class="truncate text-[15px] font-medium leading-[1.25] tracking-[0.15px] text-strong">
                {methodLabel(method())}
              </div>
              <div class="mt-[2px] text-xs leading-[1.25] tracking-[0.12px] text-text">
                Empreinte de 5 € avant chaque trajet
              </div>
            </div>
          </div>
          <div class="screen-footer">
            <OperatedBy class="mb-4" />
            <Button
              onClick={() =>
                navigate(
                  vehicle() ? `/scan?vehicle=${encodeURIComponent(vehicle() as string)}` : "/scan",
                  { replace: true },
                )
              }
            >
              C'est parti
            </Button>
          </div>
        </div>
      </Show>
    </main>
  );
}

/** "Carte bancaire •••• 4242", or "Apple Pay" (fake in PAYMENTS=fake, or a Stripe wallet). */
function methodLabel(m: PaymentMethodView | undefined): string {
  if (m?.wallet === "apple_pay") return "Apple Pay";
  return m?.last4 ? `Carte bancaire •••• ${m.last4}` : "Carte bancaire";
}

/** Figma 04b card glyph (CSS): 40 x 26 #333338, radius 5, 5 px #8c8c99 stripe 6 px from the top. */
function CardGlyph() {
  return (
    <span class="relative h-[26px] w-10 shrink-0 rounded-[5px] bg-[#333338]" aria-hidden="true">
      <span class="absolute inset-x-0 top-[6px] h-[5px] bg-[#8c8c99]" />
    </span>
  );
}

/** Figma 04b radio row, 54 px: glyph, label, radio (radio-off / radio-on ring + dot). */
function MethodRow(props: {
  glyph: JSX.Element;
  label: string;
  checked: boolean;
  disabled: boolean;
  onSelect: () => void;
}) {
  return (
    <label class="flex h-[54px] cursor-pointer items-center gap-[15px]">
      {props.glyph}
      <span class="flex-1 text-[15px] font-medium leading-[1.25] tracking-[0.15px] text-strong">
        {props.label}
      </span>
      <input
        type="radio"
        name="method"
        class="peer sr-only"
        checked={props.checked}
        disabled={props.disabled}
        onChange={() => props.onSelect()}
      />
      <span
        class="relative mr-[4px] h-[22px] w-[22px] shrink-0 rounded-full peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-violet"
        aria-hidden="true"
      >
        <Show
          when={props.checked}
          fallback={<img src="/icons/radio-off.svg" width="22" height="22" alt="" />}
        >
          <img src="/icons/radio-on-ring.svg" width="22" height="22" alt="" />
          <img
            src="/icons/radio-on-dot.svg"
            width="10"
            height="10"
            alt=""
            class="absolute left-[6px] top-[6px]"
          />
        </Show>
      </span>
    </label>
  );
}

/**
 * Figma 04b Apple Pay badge (CSS): 40 x 26 white, 0.8 px #4d4d4d border, radius 5, "Pay"
 * Semibold 13 black. The apple is a hand-drawn glyph, not a brand file.
 */
function ApplePayBadge() {
  return (
    <span
      class="flex h-[26px] w-10 shrink-0 items-center justify-center gap-[1px] rounded-[5px] border-[0.8px] border-[#4d4d4d] bg-white text-[13px] font-semibold leading-none text-black"
      style={{ "font-family": "-apple-system, BlinkMacSystemFont, 'Helvetica Neue', sans-serif" }}
      aria-hidden="true"
    >
      <svg width="10" height="12" viewBox="0 0 14 17" fill="currentColor" class="mb-[2px]">
        <path d="M11.6 9c0-2 1.6-2.9 1.7-3-1-1.4-2.4-1.6-2.9-1.6-1.2-.1-2.4.7-3 .7s-1.6-.7-2.6-.7C3.4 4.4 2.1 5.2 1.4 6.5c-1.5 2.5-.4 6.3 1 8.4.7 1 1.5 2.1 2.6 2.1 1 0 1.4-.7 2.7-.7s1.6.7 2.7.7 1.8-1 2.5-2c.8-1.2 1.1-2.3 1.1-2.4 0 0-2.2-.8-2.4-3.6Z" />
        <path d="M9.6 2.9c.6-.7 1-1.7.9-2.7-.8 0-1.9.6-2.5 1.3-.5.6-1 1.6-.9 2.6.9.1 1.9-.5 2.5-1.2Z" />
      </svg>
      Pay
    </span>
  );
}
